import { useEffect, useState } from 'react';

import { META_COLLECT_API } from '../../../../api';
import { DontExistsItem } from '../../../../components/DontExistsItem';
import { DontExistsItemFlex } from '../../../../components/DontExistsItem/styles';
import { LoadingData } from '../../../../components/LoadingData';
import { ViewMode } from '../../../../components/PageHeader';
import { FilterQuality } from '../../../../components/SideFilterMenu/FilterQuality';
import { useSigninContext } from '../../../../context/AuthProviderPages/Signin';
import { usePageContext } from '../../../../context/usePage';
import { Paginator } from '../components/paginator';
import { ContentWrapper } from './components/content-wrapper/index';
import { ListCardsQuality } from './components/list-cards/index';
import { Table } from './components/table/index';

// Interface exata da API - apenas 6 campos retornados pelo backend
export type QualidadeItem = {
  DFCLASSIFICADO: string; // Classificação (A, B, C, etc)
  DFIDCALCANALISE: number; // ID único da análise
  DFMATRICULA: string; // Matrícula do produtor
  DFNOMEPRODUTOR: string; // Nome do produtor
  DFNOMEPROPRIEDADE: string; // Nome da propriedade
  DFCNPJCPFCEI: string; // CPF/CNPJ
};

export type QualidadeResponse = {
  data: QualidadeItem[]; // Array de itens
  hasMore: boolean; // Total de registros
  page: number; // Página atual
  limit: number; // Itens por página
  totalPages: number; // Total de páginas
  count: number; // Total de registros
};

export const PQFLQualityPage = () => {
  const { setSelectedPage } = usePageContext();
  const { path } = useSigninContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<QualidadeItem[]>([]);
  const [selectedRow, setSelectedRow] = useState<QualidadeItem | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);
  const [limit, setLimit] = useState<number>(15);
  const [page, setPage] = useState<number>(1);
  const [modifyViewMode, setModifyViewMode] = useState<ViewMode>({
    have: true,
    mode: 'card',
  });
  const [openFilterSidebar, setOpenFilterSidebar] = useState<boolean>(false);

  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState<string>(
    String(currentDate.getMonth() + 1).padStart(2, '0'),
  );
  const [selectedYear, setSelectedYear] = useState<string>(
    currentDate.getFullYear().toString(),
  );

  const fetchQualityData = async () => {
    try {
      const mesano = `${selectedMonth}/${selectedYear}`;

      const { data: response } = await META_COLLECT_API.get<QualidadeResponse>(
        '/pqfl/qualidade',
        {
          params: { mesano, page, limit },
          headers: { path },
        },
      );

      setRows(response.data || []);
      setCount(response.count || 0);
      setHasMore(response.hasMore);
    } catch (error) {
      console.error('Erro ao buscar dados de qualidade:', error);
      setRows([]);
      setCount(0);
      setHasMore(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    // fetchQualityData será chamado automaticamente pelo useEffect quando page mudar
  };

  const handleVisualize = (analysis: QualidadeItem) => {
    setSelectedRow(analysis);
    setShowModal(true);
  };

  useEffect(() => {
    setSelectedPage({ option: 'pqfl_qualidade' });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchQualityData().finally(() => setLoading(false));
  }, [limit, page, selectedMonth, selectedYear]);

  if (loading) {
    return (
      <>
        <ContentWrapper
          loading={loading}
          row={selectedRow}
          openModal={showModal}
          handleCloseModal={() => {
            setShowModal(false);
            setSelectedRow(null);
          }}
          filterAction={() => setOpenFilterSidebar(true)}
        >
          <LoadingData title="Carregando..." />
        </ContentWrapper>
        <FilterQuality
          isOpen={openFilterSidebar}
          onToggle={() => setOpenFilterSidebar(!openFilterSidebar)}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={setSelectedMonth}
          onYearChange={setSelectedYear}
          onApplyFilters={handleApplyFilters}
          loading={loading}
        />
      </>
    );
  }

  return (
    <>
      <ContentWrapper
        loading={loading}
        row={selectedRow}
        openModal={showModal}
        handleCloseModal={() => {
          setShowModal(false);
          setSelectedRow(null);
        }}
        modifyViewMode={modifyViewMode}
        setModifyViewMode={setModifyViewMode}
        filterAction={() => setOpenFilterSidebar(true)}
      >
        <div
          style={{
            padding: '10px 20px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <strong style={{ fontSize: '14px', color: '#333' }}>
            Competência:
          </strong>
          <span style={{ fontSize: '14px', color: '#666' }}>
            {selectedMonth}/{selectedYear}
          </span>
        </div>

        {rows.length ? (
          <>
            {modifyViewMode.mode === 'normal' ? (
              <Table
                rows={rows}
                setSelectedRow={setSelectedRow}
                setShowModal={setShowModal}
              />
            ) : (
              <ListCardsQuality data={rows} onVisualize={handleVisualize} />
            )}
            <Paginator
              size={rows.length}
              limit={limit}
              setLimit={setLimit}
              page={page}
              setPage={setPage}
              hasMore={hasMore}
              count={count}
            />
          </>
        ) : (
          <DontExistsItemFlex>
            <DontExistsItem title="Nenhuma análise encotrada" />
          </DontExistsItemFlex>
        )}
      </ContentWrapper>
      <FilterQuality
        isOpen={openFilterSidebar}
        onToggle={() => setOpenFilterSidebar(!openFilterSidebar)}
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
        onApplyFilters={handleApplyFilters}
        loading={loading}
      />
    </>
  );
};
